import { useEffect, useRef } from 'react';
import { Popover } from 'bootstrap';
import { generatePopoverContent } from '../../../util/util';
import { logError } from '../../../util/telemetry/error/log-error.util';
import { sanitizeHtml } from '../../../util/sanitizeHtml';
import { useAppSelector } from '../../../hooks/redux';
import { selectUser } from '../../../api-integration/common.slice';

type NotificationType = {
    NOTIFICATION: string;
    REDIRECTURL: string;
    OBJTYPE: string;
    NOTIFICATIONAGE: string;
    OBJID: number;
};

const EMPTY_NOTIFICATIONS: NotificationType[] = [];

const useInitializePopovers = () => {
    const user  = useAppSelector(selectUser);

    const notifications: NotificationType[] = (user?.userNotifications as NotificationType[]) || EMPTY_NOTIFICATIONS;

    const popoverRef = useRef<Popover | null>(null);    // Global tracker for the currently open popover to open/close the same instance of popover
    const cleanupRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        const initializePopover = () => {
            // Check that particular popover exists
            const popoverTriggerElement = document.querySelector('[data-notification-popover="true"]');
            if (!popoverTriggerElement) return;
            // Dispose previous instance before creating a new one
            if (cleanupRef.current) {
                cleanupRef.current();
                cleanupRef.current = null;
            }
            if (popoverRef.current) {
                popoverRef.current.dispose();
                popoverRef.current = null;
            }

            try {
                // Sanitize untrusted fields to prevent XSS while preserving our HTML template
                const sanitizedNotifications = notifications.map(notif => {
                    // Strictly validate URLs: must be a relative path or https://
                    // If it's a javascript: protocol or breaks out, default to '#'
                    let safeUrl = '#';
                    if (notif.REDIRECTURL) {
                        const trimmedUrl = notif.REDIRECTURL.trim();
                        // Only allow safe URL schemes to prevent javascript: XSS and protocol-relative URLs (open redirect)
                        if ((trimmedUrl.startsWith('/') && !trimmedUrl.startsWith('//')) || trimmedUrl.startsWith('https://')) {
                            // URL encode to prevent attribute breakout (e.g. " onmouseover="...)
                            safeUrl = encodeURI(trimmedUrl);
                        }
                    }

                    return {
                        ...notif,
                        NOTIFICATION: sanitizeHtml(notif.NOTIFICATION),
                        REDIRECTURL: safeUrl,
                        NOTIFICATIONAGE: sanitizeHtml(notif.NOTIFICATIONAGE)
                    };
                });

                // We handle the popover manually
                const popover = new Popover(popoverTriggerElement, {
                    html: true,
                    sanitize: false, // content is pre-sanitized
                    trigger: 'manual',
                    content: generatePopoverContent(sanitizedNotifications)
                });

                popoverRef.current = popover;

                const handlePopoverShown = () => {
                    const closeBtn = document.getElementById('popover-close-btn');

                    if (closeBtn) {
                        // Remove any existing click listeners to prevent duplicates
                        closeBtn.onclick = null;
                        
                        // Add click event listener
                        const handleCloseClick = (e: MouseEvent) => {
                            e.preventDefault();
                            e.stopPropagation();
                            popover.hide();
                        };
                        
                        closeBtn.addEventListener('click', handleCloseClick, { once: false });
                    }
                };

                const handleToggle = (e: Event) => {
                    e.stopPropagation();
                    const isShown = popoverTriggerElement.getAttribute('aria-describedby');

                    if (isShown) {
                        popover.hide();
                    } else {
                        popover.show();
                    }
                };

                // Close popover on clicking X button
                popoverTriggerElement.addEventListener('shown.bs.popover', handlePopoverShown as EventListener);
                popoverTriggerElement.addEventListener('click', handleToggle);

                cleanupRef.current = () => {
                    popoverTriggerElement.removeEventListener(
                        'shown.bs.popover',
                        handlePopoverShown as EventListener
                    );

                    popoverTriggerElement.removeEventListener('click', handleToggle);
                };
            } catch (error: unknown) {
                logError(error, { category: 'unhandled_exception', context: 'useInitializePopovers' });
            }
        };

        // Initialize immediately in case the element is already there
        initializePopover();

        // Listen for the custom event emitted by Card.tsx
        document.addEventListener('notificationBellMounted', initializePopover);

        return () => {
            document.removeEventListener('notificationBellMounted', initializePopover);
            if (cleanupRef.current) {
                cleanupRef.current();
                cleanupRef.current = null;
            }
            if (popoverRef.current) {
                popoverRef.current.dispose();
                popoverRef.current = null;
            }
        };
    }, [notifications]);
};

export default useInitializePopovers;